/*
 * Radiance Veil — per-section slider spacing and mobile layout (snippets/rv-section-controls.liquid).
 *
 * The theme's sliders (Swiper) set their card gap and cards-per-view in JavaScript, so a CSS gap
 * change alone would break the slide widths and arrow math. Instead this updates each slider's own
 * Swiper settings from the section's settings and lets Swiper recalculate:
 *   - card gap: data-gap-m below 750px, data-gap-d at 750px and up
 *   - cards per row on mobile: data-per-row-m (1 / 1.5 / 2 / 2.5; decimals peek the next card)
 *   - "Stacked grid" on mobile (data-display-m="grid"): the slider is switched off below 750px
 *     and the slides lay out as a grid (CSS in the snippet), switched back on at 750px+
 * It also moves a banner's subheading right after its heading.
 * Works in the theme editor: re-applies when a section is re-rendered and when the preview resizes.
 */
(function () {
  var DESKTOP = window.matchMedia('(min-width: 750px)');

  function readConfig(section) {
    var el = section.querySelector('[data-rv-section-controls]');
    if (!el) return null;
    return {
      gapD: parseFloat(el.getAttribute('data-gap-d')),
      gapM: parseFloat(el.getAttribute('data-gap-m')),
      perRowM: el.hasAttribute('data-per-row-m') ? parseFloat(el.getAttribute('data-per-row-m')) : null,
      displayM: el.getAttribute('data-display-m') || 'default'
    };
  }

  function tuneSwiper(container, cfg) {
    var swiper = container.swiper;
    if (!swiper || swiper.destroyed) return false;
    var original = swiper.originalParams || swiper.params;
    var breakpoints = original.breakpoints || {};

    if (isFinite(cfg.gapM)) original.spaceBetween = cfg.gapM;
    if (cfg.perRowM) original.slidesPerView = cfg.perRowM;

    // Theme breakpoints are 720 and 1200. Below 750 counts as mobile, so 720–749 keeps the mobile
    // gap; a 750 breakpoint copies the 720 layout with the desktop gap.
    if (breakpoints[720] && !breakpoints[750]) {
      breakpoints[750] = Object.assign({}, breakpoints[720]);
    }
    Object.keys(breakpoints).forEach(function (key) {
      var width = parseInt(key, 10);
      if (!isFinite(width)) return;
      breakpoints[key].spaceBetween = width >= 750 ? (isFinite(cfg.gapD) ? cfg.gapD : breakpoints[key].spaceBetween)
                                                   : (isFinite(cfg.gapM) ? cfg.gapM : breakpoints[key].spaceBetween);
    });
    original.breakpoints = breakpoints;
    swiper.params.breakpoints = breakpoints;

    swiper.currentBreakpoint = undefined;
    if (typeof swiper.setBreakpoint === 'function') swiper.setBreakpoint();
    if (!swiper.currentBreakpoint || !breakpoints[swiper.currentBreakpoint]) {
      swiper.params.spaceBetween = original.spaceBetween;
      swiper.params.slidesPerView = original.slidesPerView;
    }
    applyStack(container, cfg);
    swiper.update();
    return true;
  }

  function applyStack(container, cfg) {
    var swiper = container.swiper;
    var stack = cfg.displayM === 'grid' && !DESKTOP.matches;
    container.classList.toggle('rv-stacked', stack);
    if (!swiper) return;
    if (stack && swiper.enabled !== false && typeof swiper.disable === 'function') swiper.disable();
    if (!stack && swiper.enabled === false && typeof swiper.enable === 'function') swiper.enable();
  }

  // The 'below the grid' button copy is rendered at the end of the section; it belongs at the end of
  // the section's inner container so it lines up with the grid.
  function placeBelowButton(section) {
    var below = section.querySelector('.rv-below-button');
    if (!below || below.__rvPlaced) return;
    var inner = section.querySelector('.section-inner');
    if (!inner) return;
    inner.appendChild(below);
    below.__rvPlaced = true;
  }

  function moveSubheading(section) {
    var sub = section.querySelector('[data-rv-subheading]');
    if (!sub || sub.__rvPlaced) return;
    var heading = section.querySelector('.section-blocks__heading, [class*="__content"] h1, [class*="__content"] h2, [class*="__content"] h3, [class*="__content"] h4, [class*="__text-container"] h2, [class*="__text-container"] h3');
    if (!heading) return;
    heading.insertAdjacentElement('afterend', sub);
    sub.hidden = false;
    sub.__rvPlaced = true;
  }

  function initSection(section) {
    var cfg = readConfig(section);
    if (!cfg) return;
    section.__rvSections = cfg;
    moveSubheading(section);
    placeBelowButton(section);
    section.querySelectorAll('.swiper').forEach(function (container) {
      if (container.__rvWaiting) return;
      // The theme creates the slider after loading Swiper on demand: wait for it (up to ~8s).
      container.__rvWaiting = true;
      var tries = 0;
      (function wait() {
        if (container.swiper && container.__rvTuned === container.swiper) { container.__rvWaiting = false; return; }
        if (tuneSwiper(container, cfg)) {
          container.__rvTuned = container.swiper;
          container.__rvWaiting = false;
          return;
        }
        if (++tries < 40) setTimeout(wait, 200);
        else container.__rvWaiting = false;
      })();
    });
    // Recommended products / recently viewed swap their content in after the page loads: re-apply then.
    if (!section.__rvObserver && 'MutationObserver' in window) {
      var queued = false;
      section.__rvObserver = new MutationObserver(function () {
        if (queued) return;
        queued = true;
        setTimeout(function () { queued = false; initSection(section); }, 150);
      });
      section.__rvObserver.observe(section, { childList: true, subtree: true });
    }
  }

  function initAll(root) {
    (root || document).querySelectorAll('.shopify-section').forEach(function (section) {
      if (section.querySelector('[data-rv-section-controls]')) initSection(section);
    });
  }

  function onBreakpoint() {
    document.querySelectorAll('.shopify-section').forEach(function (section) {
      if (!section.__rvSections) return;
      section.querySelectorAll('.swiper').forEach(function (container) {
        applyStack(container, section.__rvSections);
        if (container.swiper) container.swiper.update();
      });
    });
  }
  if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', onBreakpoint);
  else if (DESKTOP.addListener) DESKTOP.addListener(onBreakpoint);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: a section re-renders (new settings) → its slider is rebuilt by the theme → re-apply.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.closest ? event.target.closest('.shopify-section') || event.target : event.target;
    initSection(section);
  });
})();

/*
 * Policy & help pages (default page template's .rte--page and Shopify's /policies pages): their section
 * titles were pasted in as bold-only paragraphs (<p><strong>…</strong></p>), which CSS can't tell apart
 * from a paragraph that merely starts in bold, so they're tagged .rv-rte-title here for rv-sections.css.
 */
(function () {
  function tagTitles(root) {
    (root || document).querySelectorAll('.rte--page p, .shopify-policy__body p').forEach(function (p) {
      var bold = p.querySelector('strong, b');
      var text = p.textContent.trim();
      if (bold && text && text.length <= 90 && bold.textContent.trim() === text) p.classList.add('rv-rte-title');
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { tagTitles(); });
  else tagTitles();

  document.addEventListener('shopify:section:load', function (event) { tagTitles(event.target); });
})();
